import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";
import { ORIGEN, PRODUCTO } from "../shared/ypf";
import { TRELEW_ESTACION } from "../shared/ypfTrelew";

/* El precio del gasoil que pagamos, para el sector YPF del hub.
 *
 * Igna, 04/10/2026: sólo el valor que declara Carlos Becerra. FlotaBot lee el
 * mail «Precio D-500 Otamendi Caleta» y guarda la fila en `ypf_precio_pagado`
 * con `origen = 'declarado'`. Ése es el número de Caleta: para Caleta ya no se
 * leen `ypf_precios` (el simulador de YPF Ruta) ni `ypf_descuento` (el 4% sobre
 * el aviso de YPF), porque lo declarado se paga tal cual.
 *
 * Igna, 06/10/2026: Trelew sí. Allá nadie declara, y lo que se paga es el
 * precio de YPF Ruta menos el 4%. Por eso volvieron `preciosTrelew` y
 * `descuentos`, al final de este archivo, y SÓLO para la tarjeta de Trelew.
 * Nico, 07/10/2026: el de Trelew es el de la estación MICA, sola, sin
 * promediar (ver `preciosTrelew`).
 *
 * La tabla la escribe sólo el bot con `service_role`; el hub lee con `ver.ypf`.
 * Sin ese permiso la consulta vuelve vacía aunque se la llame desde la
 * consola: la pared es la RLS, no el menú.
 *
 * SE PIDEN SÓLO LAS DECLARADAS DEL D-500, en la base: los avisos de YPF siguen
 * entrando a la misma tabla (`origen = 'aviso_ypf'`, cuatro productos por
 * aumento) y traerlos para tirarlos en el navegador es arrastrar filas de
 * gusto. El índice `ypf_precio_pagado_origen` es justo (origen, producto,
 * vigente_desde). La estación NO se filtra acá: «001501» y «01501» son la
 * misma, y un `eq` de texto se perdería una; eso lo hace `declarados()`, que
 * además vuelve a mirar el origen por si alguien llama con otras filas.
 *
 * SE TRAEN TODAS, paginadas con `traerTodas`: Carlos declara cuando cambia el
 * precio —un par de filas por mes—, y con todas en la mano salen sin otro
 * pedido el precio de hoy, el anterior (la variación), el próximo y el
 * historial. Paginar es barato y no deja un tope que adivinar. El orden
 * termina en `id` porque paginar con empates pierde filas (ver `paginar.js`).
 *
 * Un error se TIRA: vuelto como lista vacía, la pantalla diría «todavía no hay
 * precio declarado» con la base caída, y eso manda a esperar un mail en vez de
 * avisar que algo está roto. */
const COLS =
  "id, cod_eess, producto, descripcion, precio, vigente_desde, origen, fuente";

export async function preciosDeclarados() {
  return traerTodas(() =>
    supabase
      .from("ypf_precio_pagado")
      .select(COLS, { count: "exact" })
      .eq("origen", ORIGEN)
      .eq("producto", PRODUCTO)
      .order("vigente_desde", { ascending: true })
      .order("id", { ascending: true }),
  );
}

/* La cuenta en Otamendi (`ypf_saldo`): la planilla entera de Carlos Becerra.
 *
 * LA LEE SÓLO QUIEN TIENE `ver.ypf_cuenta` (Igna, 05/10/2026: «que solo
 * tengamos permiso para ver esos datos Nicolás Cantarini y yo»). La pared es
 * la RLS de la tabla; la pantalla además no llama a estas funciones sin el
 * permiso, para que quien ve el precio no pida una tabla que no le toca.
 *
 * DOS PEDIDOS Y NO UNO. La lista de mails (`cuentasOtamendi`) va sin la
 * planilla: cada una son cientos de filas de «Movimientos», y traer todas
 * para mostrar una sería arrastrar megas de gusto. La planilla se pide de a
 * una, la que se eligió (`planillaOtamendi`). Tampoco se trae
 * `texto_planilla`, el volcado para leer en la base.
 *
 * La lista va con `limit` y sin paginar: Carlos manda la planilla cada tanto,
 * y las últimas `CUENTAS_EN_LISTA` alcanzan para elegir.
 *
 * Un error se TIRA: vuelto como lista vacía, la pantalla diría «todavía no
 * hay planilla» con la base caída, y eso manda a esperar un mail en vez de
 * avisar que algo está roto. */
export const CUENTAS_EN_LISTA = 60;
const COLS_CUENTA = "id, cod_eess, saldo, al, detalle, fuente, creado_en";

export async function cuentasOtamendi() {
  const { data, error } = await supabase
    .from("ypf_saldo")
    .select(COLS_CUENTA)
    .order("al", { ascending: false })
    .order("creado_en", { ascending: false })
    .order("id", { ascending: false })
    .limit(CUENTAS_EN_LISTA);
  if (error) throw error;
  return data || [];
}

/* La planilla de una fila, o null si esa fila no la tiene (las que entraron
 * antes de que se guardara entera). */
export async function planillaOtamendi(id) {
  const { data, error } = await supabase
    .from("ypf_saldo")
    .select("id, planilla")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data?.planilla ?? null;
}

/* ── Trelew: YPF Ruta menos el descuento (Igna, 06/10/2026) ────────────────
 *
 * «Nico me reclama el valor del combustible de Trelew. Ése sí es el valor de
 * YPF en ruta −4%». La cuenta está en `shared/ypfTrelew.js`; acá sólo se lee.
 * Las dos tablas tienen la misma pared que las de Caleta: RLS de lectura con
 * `ver.ypf` (migraciones 20260918204621 y 20261003033000).
 */

/* El precio de Trelew en `ypf_precios`: la última fila de la estación MICA.
 *
 * Nico, 07/10/2026: «el precio de Trelew es de la estación MICA. De ninguna
 * otra. Sin sacar promedio de nada.» Así que ya no se trae el día entero de
 * Trelew para promediarlo (como desde el 06/10): se trae SÓLO la última fila
 * de MICA, por su código (`TRELEW_ESTACION`, «29041»). El `eq` de texto va
 * sin miedo a los ceros de adelante: el simulador escribe los códigos de
 * Trelew siempre con cinco cifras («01970», «29041»), y «29041» ya las tiene.
 *
 * DOS PEDIDOS, en paralelo:
 *   · la última fila de MICA (`limit(1)` por fecha: la tabla es única por
 *     fecha + estación, no hay empates), y
 *   · cuál es el último día de Trelew, de cualquier estación (una fila, sólo
 *     `fecha`). Ningún precio sale de este: sirve para saber si MICA quedó
 *     atrás de un barrido y decirlo («último dato del dd/mm») en vez de
 *     mostrar un precio viejo como si fuera de hoy.
 * Ninguno de los dos trae las otras estaciones, así que no hay con qué
 * promediar ni reemplazar a MICA por error.
 *
 * La localidad va con `ilike` sin comodines: es igualdad sin mayúsculas. El
 * simulador escribe «TRELEW», y si un día escribe «Trelew» es la misma.
 *
 * Vuelve `{ ultimoDia, filas }`: `ultimoDia` es «2026-10-07» o null; `filas`,
 * la fila de MICA o nada. Un error se TIRA: vuelto como vacío, la tarjeta
 * diría «todavía no hay precio de MICA» con la base caída. */
const COLS_PRECIOS = "fecha, cod_eess, eess, localidad, provincia, gasoil_g2, gasoil_g3";
export const LOCALIDAD_TRELEW = "trelew";

export async function preciosTrelew() {
  const [dia, mica] = await Promise.all([
    supabase
      .from("ypf_precios")
      .select("fecha")
      .ilike("localidad", LOCALIDAD_TRELEW)
      .order("fecha", { ascending: false })
      .limit(1),
    supabase
      .from("ypf_precios")
      .select(COLS_PRECIOS)
      .eq("cod_eess", TRELEW_ESTACION)
      .order("fecha", { ascending: false })
      .limit(1),
  ]);
  if (dia.error) throw dia.error;
  if (mica.error) throw mica.error;
  return { ultimoDia: dia.data?.[0]?.fecha ?? null, filas: mica.data || [] };
}

/* `ypf_descuento` entera. Son dos filas (08 y 06, 4%) y crece una o dos por
 * renegociación; la regla de cuál aplica —la del producto, la más reciente
 * que ya valía— está en `porcentajeTrelew` y necesita todas a mano. Paginada
 * igual y ordenada hasta `id`, para que no haya empates.
 *
 * Un error se TIRA, y la pantalla lo atrapa aparte del de los precios: sin
 * descuento se muestra el precio de lista rotulado como de lista, nunca el de
 * lista como si fuera 0%. */
const COLS_DESCUENTO = "id, cod_eess, producto, porcentaje, vigente_desde";

export async function descuentos() {
  return traerTodas(() =>
    supabase
      .from("ypf_descuento")
      .select(COLS_DESCUENTO, { count: "exact" })
      .order("vigente_desde", { ascending: true })
      .order("id", { ascending: true }),
  );
}
